'use client';

import React from 'react';
import { Users } from 'lucide-react';
import { useSport } from '../../contexts/SportContext';
import { teamNames } from '../../constants/teamNames';
import { TeamLogo } from '../TeamLogo';
import { FilterShell } from './filters/FilterShell';
import { FilterChip } from './filters/FilterChip';
import { ConfirmedLineupToggle } from './filters/ConfirmedLineupToggle';

interface TeamFilterLocalProps {
  selectedTeams: Set<string>;
  onTeamsChange: (teams: Set<string>) => void;
  availableTeams: string[];
  teamsWithQuestionable?: Set<string>;
  isCollapsed?: boolean;
  /** Controlled chevron expand state (lifted so the panel can lay collapsed
   *  Teams/Markets headers side-by-side). */
  expanded?: boolean;
  onExpandedChange?: (next: boolean) => void;
  /** MLB-only — renders the "Confirmed lineup" toggle far-right in the header. */
  confirmedLineupsOnly?: boolean;
  onConfirmedLineupsOnlyChange?: (next: boolean) => void;
  className?: string;
}

interface TeamChipProps {
  team: string;
  isSelected: boolean;
  hasQuestionable?: boolean;
  onToggle: (team: string) => void;
}

function TeamChip({ team, isSelected, hasQuestionable, onToggle }: TeamChipProps) {
  const sport = useSport();
  const teamName = teamNames[team] || team;

  return (
    <FilterChip
      isSelected={isSelected}
      onClick={() => onToggle(team)}
      title={teamName}
      tone={hasQuestionable ? 'questionable' : 'default'}
    >
      <TeamLogo team={team} sport={sport} className="w-3.5 h-3.5" />
      <span className="uppercase font-semibold">{team}</span>
    </FilterChip>
  );
}

export function TeamFilterLocal({
  selectedTeams,
  onTeamsChange,
  availableTeams,
  teamsWithQuestionable,
  isCollapsed = false,
  expanded,
  onExpandedChange,
  confirmedLineupsOnly,
  onConfirmedLineupsOnlyChange,
  className,
}: TeamFilterLocalProps) {
  const sport = useSport();
  const showConfirmedToggle = sport === 'mlb' && onConfirmedLineupsOnlyChange !== undefined;
  const selectedCount = selectedTeams.size;
  const noneSelected = selectedCount === 0;

  const handleTeamToggle = (team: string) => {
    const next = new Set(selectedTeams);
    if (next.has(team)) next.delete(team);
    else next.add(team);
    onTeamsChange(next);
  };

  const handleClearAll = () => onTeamsChange(new Set());

  const collapsedTooltip = noneSelected
    ? 'All teams shown'
    : selectedCount === availableTeams.length
      ? 'All teams'
      : Array.from(selectedTeams).map(t => teamNames[t] || t).join(', ');

  return (
    <FilterShell
      icon={Users}
      label="Teams"
      isCollapsed={isCollapsed}
      defaultExpanded={true}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      collapsedTooltip={isCollapsed ? `Filter teams: ${collapsedTooltip}` : collapsedTooltip}
      collapsedBadge={selectedCount > 0 ? selectedCount : undefined}
      onCollapsedClick={() => {
        if (!noneSelected) onTeamsChange(new Set());
      }}
      collapsedClickDisabled={noneSelected}
      onClearAll={noneSelected ? undefined : handleClearAll}
      summary={
        selectedCount > 0 && selectedCount < availableTeams.length ? (
          <span className="text-xs text-brand-600 dark:text-brand-400 font-medium">
            {selectedCount} selected
          </span>
        ) : null
      }
      headerExtra={
        showConfirmedToggle ? (
          <ConfirmedLineupToggle
            value={confirmedLineupsOnly ?? false}
            onChange={onConfirmedLineupsOnlyChange!}
          />
        ) : undefined
      }
      chipsMaxHClass="max-h-32"
      className={className}
    >
      {availableTeams.map(team => (
        <TeamChip
          key={team}
          team={team}
          isSelected={selectedTeams.has(team)}
          hasQuestionable={teamsWithQuestionable?.has(team)}
          onToggle={handleTeamToggle}
        />
      ))}
    </FilterShell>
  );
}
